import { Loader2, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { KnowledgeDocumentDetail } from "../../domain/entities/document"
import { StatusBadge } from "./status-badge"
import { TypeBadge } from "./type-badge"

const formatDate = (value: string | null): string => {
  if (!value) return "—"
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString()
}

// Date-only (validity window) — no time-of-day component.
const formatDay = (value: string | null): string => {
  if (!value) return "—"
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString()
}

const splitEntities = (raw: string): string[] =>
  raw
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)

const MetadataBadge = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
    {children}
  </span>
)

const formatDomainMetadataKey = (key: string): string => {
  const label = key.replaceAll("_", " ")
  if (label.length === 0) return label
  return label.charAt(0).toUpperCase() + label.slice(1)
}

const formatObjectFieldLabel = (key: string): string =>
  key
    .replaceAll("_", " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const tryParseJsonValue = (value: unknown): unknown => {
  if (typeof value !== "string") return value
  const trimmed = value.trim()
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      return JSON.parse(trimmed) as unknown
    } catch {
      return value
    }
  }
  return value
}

const formatObjectFieldValue = (value: unknown): string => {
  if (value === null || value === undefined) return "---"
  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

const formatPlainObject = (obj: Record<string, unknown>): string =>
  Object.entries(obj)
    .map(
      ([key, val]) =>
        `${formatObjectFieldLabel(key)}: ${formatObjectFieldValue(val)}`,
    )
    .join("\n")

const formatOrderedObjects = (items: Record<string, unknown>[]): string =>
  items
    .map((obj, index) => `${index + 1}. \n${formatPlainObject(obj)}`)
    .join("\n\n")

const formatDomainMetadataValue = (value: unknown): string => {
  const parsed = tryParseJsonValue(value)
  if (parsed === null || parsed === undefined) return "—"

  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return "—"
    if (parsed.every(isPlainObject)) {
      return formatOrderedObjects(parsed)
    }
    const parts = parsed.map((item) => {
      if (item === null || item === undefined) return ""
      if (isPlainObject(item)) return formatPlainObject(item)
      return String(item)
    })
    const joined = parts.filter((s) => s.length > 0).join(", ")
    return joined.length > 0 ? joined : "—"
  }

  if (isPlainObject(parsed)) {
    const body = formatPlainObject(parsed)
    return body.length > 0 ? `1. \n${body}` : "—"
  }

  return String(parsed)
}

export type DocumentMetadataPanelProps = {
  doc: KnowledgeDocumentDetail
  kbName: string | undefined
  isExtracting: boolean
  isRedoing: boolean
  canRedoMetadata: boolean
  onRedo: () => void
  redoPending: boolean
}

// Metadata is read-only in the UI: manual editing was removed (the backend PUT
// /documents/{id}/metadata endpoint is gone) so the sole edit source of truth is
// re-extraction. `Re-create metadata` (redo) re-runs extraction end to end.
export const DocumentMetadataPanel = ({
  doc,
  kbName,
  isExtracting,
  isRedoing,
  canRedoMetadata,
  onRedo,
  redoPending,
}: DocumentMetadataPanelProps) => {
  return (
    <div className="space-y-5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={doc.status} />
        <TypeBadge type={doc.documentType} />
        {canRedoMetadata && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={onRedo}
            disabled={redoPending}
          >
            {redoPending ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            Re-create metadata
          </Button>
        )}
      </div>

      <dl className="grid gap-2 sm:grid-cols-[auto_1fr] sm:gap-x-4">
        <dt className="text-muted-foreground">Knowledge base</dt>
        <dd className="min-w-0 break-words">{kbName ?? "—"}</dd>

        <dt className="text-muted-foreground">Created</dt>
        <dd>{formatDate(doc.createdAt)}</dd>

        <dt className="text-muted-foreground">Updated</dt>
        <dd>{formatDate(doc.updatedAt)}</dd>
      </dl>

      {isExtracting || isRedoing ? (
        <div className="flex flex-col items-center gap-3 border-t py-10 text-sm text-muted-foreground">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p>Extracting metadata…</p>
        </div>
      ) : doc.metadata ? (
        <div className="space-y-4 border-t pt-4">
          <h3 className="text-sm font-medium">Metadata</h3>

          <dl className="grid gap-2 sm:grid-cols-[auto_1fr] sm:items-center sm:gap-x-4">
            <dt className="text-muted-foreground">Doc type</dt>
            <dd>
              <MetadataBadge>{doc.metadata.docType}</MetadataBadge>
            </dd>

            <dt className="text-muted-foreground">Domain</dt>
            <dd>
              <MetadataBadge>{doc.metadata.domain}</MetadataBadge>
            </dd>

            {doc.metadata.docCode && (
              <>
                <dt className="text-muted-foreground">Doc code</dt>
                <dd className="font-mono break-words">{doc.metadata.docCode}</dd>
              </>
            )}

            {(doc.metadata.validFrom || doc.metadata.validTo) && (
              <>
                <dt className="text-muted-foreground">Valid</dt>
                <dd>
                  {formatDay(doc.metadata.validFrom)} → {formatDay(doc.metadata.validTo)}
                </dd>
              </>
            )}
          </dl>

          {doc.metadata.topics.length > 0 && (
            <div>
              <p className="mb-2 text-muted-foreground">
                Topics ({doc.metadata.topics.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {doc.metadata.topics.map((topic, index) => (
                  <MetadataBadge key={`${topic}-${index}`}>{topic}</MetadataBadge>
                ))}
              </div>
            </div>
          )}

          {doc.metadata.titles.length > 0 && (
            <div>
              <p className="mb-2 text-muted-foreground">
                Titles ({doc.metadata.titles.length})
              </p>
              <ul className="list-disc space-y-0.5 pl-5 break-words">
                {doc.metadata.titles.map((title, index) => (
                  <li key={`${title}-${index}`}>{title}</li>
                ))}
              </ul>
            </div>
          )}

          {(() => {
            const domainMeta = doc.metadata.domainSpecificMetadata
            const rows = Object.entries(domainMeta ?? {})
            if (rows.length === 0) return null
            return (
              <div>
                <p className="mb-2 text-muted-foreground">
                  Domain-specific metadata
                </p>
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="table-fixed w-full min-w-[12rem] border-collapse text-left text-sm">
                    <colgroup>
                      <col className="w-1/3" />
                      <col className="w-2/3" />
                    </colgroup>
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-3 py-2 font-medium text-muted-foreground">
                          Key
                        </th>
                        <th className="px-3 py-2 font-medium text-muted-foreground">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(([key, value]) => (
                        <tr
                          key={key}
                          className="border-b border-border last:border-b-0"
                        >
                          <td className="min-w-0 align-top px-3 py-2 font-medium break-words text-foreground">
                            {formatDomainMetadataKey(key)}
                          </td>
                          <td className="min-w-0 px-3 py-2 align-top text-xs text-foreground">
                            <span className="block font-mono whitespace-pre-wrap break-words">
                              {formatDomainMetadataValue(value)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })()}

          {(() => {
            // Prefer the structured `entities` routing list; fall back to the
            // legacy semicolon `keyEntities` for docs not yet backfilled (P6).
            const entities =
              doc.metadata.entities.length > 0
                ? doc.metadata.entities
                : splitEntities(doc.metadata.keyEntities)
            return (
              <div>
                <p className="mb-2 text-muted-foreground">
                  Entities ({entities.length})
                </p>
                {entities.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {entities.map((entity, index) => (
                      <MetadataBadge key={`${entity}-${index}`}>
                        {entity}
                      </MetadataBadge>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">—</p>
                )}
              </div>
            )
          })()}
        </div>
      ) : (
        <p className="border-t pt-4 text-muted-foreground">
          No metadata extracted yet.
        </p>
      )}
    </div>
  )
}
